import { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MapPin, ChevronRight } from "lucide-react";

const programImages = {
  xc: "https://images.unsplash.com/photo-1754546552679-9c742012bbd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxtb3VudGFpbiUyMGJpa2UlMjBjcm9zcyUyMGNvdW50cnklMjBYQyUyMHJhY2UlMjBlbGl0ZXxlbnwwfHx8fDE3NzU5NDAxNzl8MA&ixlib=rb-4.1.0&q=85",
  downhill: "https://images.unsplash.com/photo-1548696509-70ea53cdd2cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxkb3duaGlsbCUyMG1vdW50YWluJTIwYmlrZSUyMHJpZGVyJTIwZm9yZXN0JTIwbXVkJTIwc3BlZWR8ZW58MHx8fHwxNzc1OTQwMTkyfDA&ixlib=rb-4.1.0&q=85",
  enduro: "https://images.unsplash.com/photo-1720724075506-e91e3d4aa4fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxtb3VudGFpbiUyMGJpa2UlMjBlbmR1cm8lMjByb2NreSUyMHRyYWlsJTIwcmlkZXIlMjBhY3Rpb258ZW58MHx8fHwxNzc1OTQwMTkyfDA&ixlib=rb-4.1.0&q=85",
  freeride: "https://images.unsplash.com/photo-1569062339144-c568be24ad9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxtb3VudGFpbiUyMGJpa2UlMjBlcGljJTIwanVtcCUyMHN1bnNldCUyMHNpbGhvdWV0dGUlMjBleHRyZW1lfGVufDB8fHx8MTc3NTk0MDE3OXww&ixlib=rb-4.1.0&q=85",
};

export default function Programs() {
  const { t } = useLang();
  const [selected, setSelected] = useState(null);

  const disciplines = [
    { key: "xc", data: t.programs.xc, img: programImages.xc },
    { key: "downhill", data: t.programs.downhill, img: programImages.downhill },
    { key: "enduro", data: t.programs.enduro, img: programImages.enduro },
    { key: "freeride", data: t.programs.freeride, img: programImages.freeride },
  ];

  const selectedData = selected
    ? disciplines.find((d) => d.key === selected)
    : null;

  return (
    <section
      id="programs"
      data-testid="programs-section"
      className="py-24 sm:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Label */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-[2px] bg-[#FF3B30]" />
          <span className="text-xs uppercase tracking-[0.3em] text-[#A3A3A3] font-body">
            {t.programs.label}
          </span>
        </div>

        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-16">
          {t.programs.title}
        </h2>

        {/* Programs Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {disciplines.map((d, i) => (
            <button
              key={d.key}
              data-testid={`program-card-${d.key}`}
              onClick={() => setSelected(d.key)}
              className="group relative aspect-[3/4] overflow-hidden border border-[#262626] hover:border-[#FF3B30] transition-all text-left animate-fade-in"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              {/* Background Image */}
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                style={{ backgroundImage: `url('${d.img}')` }}
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="w-8 h-[2px] bg-[#FF3B30] mb-4 transition-all group-hover:w-12" />
                <h3 className="font-heading text-2xl uppercase tracking-wider text-white mb-2">
                  {d.data.name}
                </h3>
                <p className="text-xs text-[#A3A3A3] font-body leading-relaxed mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {d.data.desc}
                </p>
                <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#FF3B30] font-body opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {t.programs.clickHint} <ChevronRight className="w-3 h-3" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent
          className="bg-[#111111] border-[#262626] text-white max-w-2xl rounded-none sm:rounded-none max-h-[85vh] overflow-y-auto"
          data-testid="program-detail-dialog"
        >
          {selectedData && (
            <>
              {/* Header Image */}
              <div className="relative h-48 -mx-6 -mt-6 mb-4 overflow-hidden">
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url('${selectedData.img}')` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-black/50 to-transparent" />
                <div className="absolute bottom-4 left-6">
                  <div className="w-10 h-[2px] bg-[#FF3B30] mb-3" />
                  <DialogHeader>
                    <DialogTitle className="font-heading text-3xl sm:text-4xl uppercase tracking-wider text-white">
                      {selectedData.data.name}
                    </DialogTitle>
                  </DialogHeader>
                </div>
              </div>

              {/* Description */}
              <DialogDescription className="text-sm text-[#A3A3A3] font-body leading-relaxed mb-6">
                {selectedData.data.details}
              </DialogDescription>

              {/* Facilities List */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4 text-[#FF3B30]" />
                  <h4 className="font-heading text-lg uppercase tracking-wider text-white">
                    {t.programs.facilities}
                  </h4>
                </div>
                <ul className="space-y-3">
                  {selectedData.data.facilitiesList.map((facility) => (
                    <li
                      key={facility}
                      className="flex items-start gap-3 text-sm text-[#A3A3A3] font-body"
                    >
                      <div className="w-1.5 h-1.5 bg-[#FF3B30] rounded-full mt-1.5 shrink-0" />
                      {facility}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <button
                data-testid="program-detail-apply-btn"
                onClick={() => {
                  setSelected(null);
                  setTimeout(() => {
                    document.getElementById("forms")?.scrollIntoView({ behavior: "smooth" });
                  }, 300);
                }}
                className="w-full mt-6 bg-[#FF3B30] text-white font-heading uppercase tracking-widest px-8 py-4 text-sm hover:bg-[#FF5C53] transition-all hover:-translate-y-0.5"
              >
                {t.nav.apply}
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
